import React, { useEffect, useRef, useState } from 'react';
import { Award, TrendingUp, Users } from 'lucide-react';
import { influencerData, images } from '../../data/mock';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const highlights = [
    { icon: Users, label: 'Global Reach', value: influencerData.monthlyReach },
    { icon: TrendingUp, label: 'Engagement Rate', value: `${influencerData.engagementRate}%` },
    { icon: Award, label: 'Brand Campaigns', value: '50+' },
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image Column */}
          <div
            className={`relative transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative">
              {/* Gold Frame */}
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#D4AF37]/30" />
              
              {/* Main Image */}
              <div className="relative overflow-hidden">
                <img
                  src={images.about}
                  alt="Alexandra Stone"
                  className="w-full h-[500px] md:h-[600px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-60" />
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-[#121212] border border-[#D4AF37]/20 px-6 py-4">
                <p className="text-[#D4AF37] text-3xl font-light">{influencerData.followers}</p>
                <p className="text-[#A1A1AA] text-xs tracking-[0.2em]">FOLLOWERS</p>
              </div>
            </div>
          </div>

          {/* Content Column */}
          <div
            className={`transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            {/* Section Label */}
            <p className="text-[#D4AF37] text-xs tracking-[0.4em] mb-4">THE STORY</p>

            {/* Headline */}
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6">
              Elevating Brands to
              <br />
              <span className="italic text-[#D4AF37]">Iconic Status</span>
            </h2>

            {/* Decorative Line */}
            <div className="w-16 h-[1px] bg-[#D4AF37] mb-8" />

            {/* Bio */}
            <p className="text-[#A1A1AA] text-lg leading-relaxed mb-8">
              {influencerData.bio}
            </p>

            <p className="text-[#A1A1AA] leading-relaxed mb-10">
              Every partnership I undertake is a commitment to excellence. I work exclusively with brands 
              that align with my values of authenticity, quality, and impact. Together, we create content 
              that doesn't just reach audiences — it moves them.
            </p>

            {/* Highlights */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
              {highlights.map((item, index) => (
                <div
                  key={item.label}
                  className={`transition-all duration-700 ease-out ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <item.icon className="text-[#D4AF37] mb-3" size={24} />
                  <p className="text-white text-xl md:text-2xl font-light">{item.value}</p>
                  <p className="text-[#A1A1AA] text-xs tracking-[0.1em] mt-1">{item.label.toUpperCase()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
